import { useCallback, useEffect, useState } from "react";
import { getOrganizationTreeList } from "src/apis/organizaApi";
import { EmployeeDto } from "src/dtos/employee/employeeDto";
import { OrganizationTreeDto } from "src/dtos/organization/organizationDto";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { showWarning } from "src/utils/toast/toastUtil";
import { OrgTreeNode } from "./OrgTreeNode";
import ModalWrapper from "../ModalWrapper";
import { SearchForm } from "../SearchForm";
import { InputForm } from "../../form/input/InputForm";
import { SendTargetEmp } from "src/dtos/popbill/CmailDto";
import { OrgNode } from "./OrgNode";

//로그 추출용 함수
interface OrgTreeProps {
	orgCd: string;
	orgNm: string;
	orgAdmin: string;
	orgChildren: OrgTreeProps[];
}
const OrgTree = (orgNode: OrganizationTreeDto): OrgTreeProps => {
	return {
		orgCd: orgNode.orgCd,
		orgNm: orgNode.orgNm,
		orgAdmin: orgNode.orgAdmin,
		orgChildren: orgNode.orgChildren?.map(child => OrgTree(child)) ?? [],
	}
}

export const OrganizaMapModal = () => {

    const { setOrganizaMapModalVisible, organizaMapModalProps } = useUiuxStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();

    const [search, setSearch] = useState('');

    const [orgList, setOrgList] = useState<OrganizationTreeDto[]>([]);
	const { employeeCdList, orgCdList } = useCommonCodeStore();
	const [processMessage, setProcessMessage] = useState('');
	const [allNodesOpen, setAllNodesOpen] = useState(true);
	const [modalSelectedEmpList, setModalSelectedEmpList] = useState<SendTargetEmp[]>([]);

	const handleToggleAllNodes = () => {
		setAllNodesOpen(prev => !prev);
	};

	//트리 사원 클릭 이벤트
	const addSelectedEmp = (emp: SendTargetEmp) => {
		if(modalSelectedEmpList?.some(item => item.empNo === emp.empNo)) {
			setModalSelectedEmpList(prev => prev?.filter(item => item.empNo !== emp.empNo));
		} else {
			setModalSelectedEmpList(prev => [...(prev || []), emp]);
		}
	}


	const handleConfirmClick = useCallback(() => {
		if (!organizaMapModalProps) return;
		const { 
			selectedEmpList = [],
			handleFoucsId,
			onConfirm = () => { alert('모달 코드 등록후 행동할 동작이 없습니다.'); },
		} = organizaMapModalProps;

		//모달에서 선택된 항목이 없으면 경고 메시지 출력
		if(modalSelectedEmpList.length === 0) { showWarning('선택된 항목이 없습니다.'); return; }

		//SendTargetEmp 변환
		const sendTargetList = modalSelectedEmpList.map(emp => ({
			empNo: emp.empNo,
			empNm: emp.empNm,
			mobileNo: emp.mobileNo ?? '',
		}));

		onConfirm(sendTargetList);

		setOrganizaMapModalVisible(false);

	}, [
		modalSelectedEmpList,
		organizaMapModalProps,
		setOrganizaMapModalVisible
	])

    const fetchData = useCallback(async () => {
        setProcessMessage('');
        setLoadingSpinner(true);
        try {
            const orgResponse = await getOrganizationTreeList('');
            if(orgResponse.data.data) {
                const orgData = orgResponse.data.data;
                const employees = employeeCdList;

                // 1. 사원 목록을 orgCd 기준으로 그룹화하여 Map을 생성합니다. (효율적인 탐색)
                const employeesByOrgCd = new Map<string, EmployeeDto[]>();
                employees.forEach(emp => {
                    if (!emp.orgCd) return;
                    if (!employeesByOrgCd.has(emp.orgCd)) {
                        employeesByOrgCd.set(emp.orgCd, []);
                    }
                    employeesByOrgCd.get(emp.orgCd)!.push(emp);
                });

                // 2. 조직도 트리를 재귀적으로 순회하며 각 노드에 사원 목록을 할당하고 부서장을 정렬합니다.
                const assignEmployeesToTree = (nodes: OrganizationTreeDto[]): OrganizationTreeDto[] => {
                    return nodes.map(node => {
                        const children = node.orgChildren ? assignEmployeesToTree(node.orgChildren) : null;
                        let empListForNode = employeesByOrgCd.get(node.orgCd) || [];

                        // 부서장(orgAdmin)이 지정되어 있고, 해당 부서에 사원이 있으면
                        if (node.orgAdmin && empListForNode.length > 0) {
                            const adminIndex = empListForNode.findIndex(emp => emp.empNo === node.orgAdmin);

                            // 사원 목록에서 부서장을 찾으면
                            if (adminIndex > -1) {
                                // 부서장 객체를 복사하고 이름에 '(부서장)'을 추가
                                const adminEmp = { 
                                    ...empListForNode[adminIndex], 
                                    empNm: `${empListForNode[adminIndex].empNm} (부서장)` 
                                };
                                
                                // 기존 사원 목록에서 부서장을 제외한 나머지 사원들
                                const otherEmployees = empListForNode.filter(emp => emp.empNo !== node.orgAdmin);
                                
                                // 부서장을 맨 앞에, 나머지 사원들을 뒤에 배치하여 새로운 목록 생성
                                empListForNode = [adminEmp, ...otherEmployees];
                            }
                        }

                        return {
                            ...node,
                            empList: empListForNode.length > 0 ? empListForNode : null,
                            orgChildren: children,
                        };
                    });
                };

                const orgTreeWithEmployees = assignEmployeesToTree(orgData);
                setOrgList(orgTreeWithEmployees);
            }
        } catch (error) {
            console.error("조직도 데이터를 불러오는 중 오류 발생:", error);
            setProcessMessage("조직도 데이터를 불러오는 중 오류가 발생했습니다.");
        } finally {
            setLoadingSpinner(false);
        }
    }, [employeeCdList, setLoadingSpinner]);

	const handleEnterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			console.log('search', search);
		}
	}

	//모달 초기값 설정 - 트리 불러오기, 선택된 사원 목록 설정
    useEffect(() => { 
		fetchData(); 
		if(organizaMapModalProps?.selectedEmpList) { setModalSelectedEmpList(organizaMapModalProps?.selectedEmpList); }
	}, [fetchData, organizaMapModalProps?.selectedEmpList])

    return <>
        <ModalWrapper size="md" overlay={true} width={1200}
            onOverlayClick={() => { setOrganizaMapModalVisible(false) }}
            onEscKeyDown={() => { setOrganizaMapModalVisible(false) }}>
            <div className="w-full h-[10%] flex items-center px-[24px] bg-gray-100">
                <p className="font-bold text-[14px]">
                    부서 조직도 검색
                </p>
            </div>
            <div className="w-full h-[10%] flex items-center px-[24px]">
                
				<InputForm
					title="부서명" 	
					value={search}
					editable={true}
					wrapperOption = {{
						wrapperStyle: "flex-1 mb-[5px]",
						titleStyle: "w-[150px] min-w-[150px]",
						inputStyle: "flex-1 mr-[8px]"
					}}
					name="search"
					onChange={(e) => setSearch(e.target.value)} 
					onKeyDown={handleEnterKeyDown}
					onLoadFocus={true}
				/>

				<button 
					onClick={handleToggleAllNodes}
					className={`w-[100px] min-w-[100px] h-[30px] text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold ml-auto`}
				>
					{allNodesOpen ? '모두 닫기' : '모두 펼치기'}
				</button>
            </div>
            <p className="text-red-500 px-[24px] text-[12px] -mt-[12px]">
                {processMessage}
            </p>
			<div className="w-full max-w-[95%] mx-auto h-[70%] max-h-[80%] overflow-auto flex flex-col gap-[4px] p-[12px] border border-gray-200">
			{search === '' && orgList.map(node => (
                <OrgTreeNode key={node.orgCd} node={node} level={0} isAllOpen={allNodesOpen} selectedEmpList={modalSelectedEmpList} addSelectedEmp={addSelectedEmp} />
            ))}
			{search !== '' && orgCdList.filter(node => node.orgNm.includes(search)).map(node => (
				<OrgNode key={node.orgCd} node={node} empList={employeeCdList.filter(emp => emp.orgCd === node.orgCd)} level={0} isAllOpen={allNodesOpen} selectedEmpList={modalSelectedEmpList} addSelectedEmp={addSelectedEmp} />
			))}
			</div>
			
		<div className="w-full h-[10%] flex justify-end items-center gap-[8px] px-[8px]">
			{/* 검증 : {organizaMapModalProps?.onClear ? '있음' : '없음'} */}
			{organizaMapModalProps?.onClear && (
			<button className={`text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold`}
				onClick={() => {
					if (organizaMapModalProps?.onClear) organizaMapModalProps?.onClear();
					setOrganizaMapModalVisible(false);
				}}>
				비우기
			</button>
			)}
			<button className={`text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold`}
				onClick={handleConfirmClick}>
				확인
			</button>
			<button className={`text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold`}
				onClick={() => setOrganizaMapModalVisible(false)}>
				취소
			</button>
		</div>
        </ModalWrapper>
    </>
}